import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { mensajeDeError } from '@/lib/supabase/errores'
import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'

/**
 * Si quien entró sale a campo.
 *
 * No se decide por el rol sino por el vínculo con un colaborador, que es el
 * hecho: un Gerente operativo que también sale a la calle necesita la pantalla
 * de campo, y alguien con rol operario que nunca se le asignó nada, no.
 */
/**
 * Igual que `useSoyDeCampo`, pero diciendo además si la respuesta ya llegó.
 *
 * Hace falta para distinguir «todavía no sé» de «ya sé que no». Quien solo
 * mira el booleano ve `false` en los dos casos, y una pantalla que decide a
 * dónde mandar a alguien no puede confundirlos: se quedaba girando para
 * siempre en «Abriendo…».
 */
export function useSoyDeCampoConEstado(): { deCampo: boolean; resolviendo: boolean } {
  const consulta = useQuery({
    queryKey: ['soy-de-campo'],
    // Cambia cuando administración vincula el usuario, no cada rato.
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<boolean> => {
      /*
       * SIN SEÑAL SIGUE SIENDO DE CAMPO.
       *
       * Esto decide qué aplicativo ve la persona al abrir. Decía `if (error)
       * return false`, y un error de red es un error: al abrir la app en la
       * obra, sin cobertura y sin nada en memoria —la caché de las consultas
       * no sobrevive a cerrar la app—, el técnico dejaba de ser de campo y se
       * encontraba con las pantallas de oficina. La puerta de entrada, rota
       * justo donde todo lo demás ya funcionaba.
       *
       * Quién es de campo no cambia de un día para otro: se recuerda.
       */
      return conRespaldo<boolean>('soy-de-campo', async () => {
        const { data, error } = await supabase.rpc('fn_soy_de_campo')

        if (error) throw new Error(mensajeDeError(error))

        return Boolean(data)
      })
    },
  })

  return {
    deCampo: consulta.data ?? false,
    // `isPending` es «no hay dato todavía»; un error ya es respuesta (no es de
    // campo), así que no deja la pantalla colgada.
    resolviendo: consulta.isPending,
  }
}

/** Solo el sí o el no, para quien no necesita saber si ya respondió. */
export function useSoyDeCampo(): boolean {
  return useSoyDeCampoConEstado().deCampo
}
